import { useNavigate } from "react-router-dom";

import {
  ArrowRightLeft,
  HandHelping,
  History,
  LayoutGrid,
  MapPin,
  PackageCheck,
  PackageX,
  Scale,
  Search,
  Warehouse,
} from "lucide-react";

import { useProfileStore } from "~/store/profileStore";

interface Section {
  title: string;
  description: string;
  icon: React.ElementType;
  url: string;
  color: string;
  superadminOnly?: boolean;
  allowedRoles?: string[];
}

const sections: Section[] = [
  {
    title: "Ingreso",
    description: "Registrar el ingreso de compras recibidas (MP, PT y ME)",
    icon: PackageCheck,
    url: "/purchasing/ingreso",
    color: "bg-emerald-50 text-emerald-600 border-emerald-200",
    allowedRoles: ["superadmin", "admin", "logistica"],
  },
  {
    title: "Traslados",
    description: "Trasladar stock entre bodegas y plantas",
    icon: ArrowRightLeft,
    url: "/purchasing/traslados",
    color: "bg-orange-50 text-orange-600 border-orange-200",
    allowedRoles: ["superadmin", "admin", "logistica"],
  },
  {
    title: "Pedidos Picking",
    description: "Ver pedidos listos para picking por cliente",
    icon: HandHelping,
    url: "/wms/picking-orders",
    color: "bg-blue-50 text-blue-600 border-blue-200",
  },
  {
    // Positions are hidden for the August go-live: keep relocation for superadmin
    // only (position-to-position moves). Bodega-to-bodega moves use Traslados.
    title: "Reubicación",
    description: "Mover pallets o inventario entre ubicaciones",
    icon: ArrowRightLeft,
    url: "/wms/relocation",
    color: "bg-amber-50 text-amber-600 border-amber-200",
    superadminOnly: true,
  },
  {
    title: "Bodegas",
    description: "Administrar bodegas, pasillos y racks",
    icon: Warehouse,
    url: "/wms/locations",
    color: "bg-slate-50 text-slate-600 border-slate-200",
    superadminOnly: true,
  },
  {
    // Position-level explorer — hidden while operating at bodega level.
    title: "Ubicaciones",
    description: "Explorar inventario por posición en bodega",
    icon: MapPin,
    url: "/wms/ubicaciones",
    color: "bg-cyan-50 text-cyan-600 border-cyan-200",
    superadminOnly: true,
  },
  {
    title: "Consulta Stock",
    description: "Consultar stock por SKU, pallet o lote, con descarga Excel",
    icon: Search,
    url: "/wms/stock-consultation",
    color: "bg-violet-50 text-violet-600 border-violet-200",
  },
  {
    title: "Kardex de Productos",
    description: "Trazabilidad de movimientos por bodega, con saldo corrido",
    icon: History,
    url: "/wms/movements",
    color: "bg-purple-50 text-purple-600 border-purple-200",
    allowedRoles: ["superadmin", "admin", "logistica", "bodega"],
  },
  {
    title: "Stock Consolidado",
    description:
      "Vista única de aceites, materias primas y productos con descarga Excel",
    icon: LayoutGrid,
    url: "/wms/consolidated-stock",
    color: "bg-indigo-50 text-indigo-600 border-indigo-200",
    allowedRoles: ["superadmin", "admin", "logistica"],
  },
  {
    title: "Devoluciones",
    description: "Gestionar devoluciones de productos",
    icon: PackageX,
    url: "/wms/returns",
    color: "bg-rose-50 text-rose-600 border-rose-200",
  },
  {
    title: "Ajustes de Stock",
    description: "Ajustar inventario por quiebre, vencimiento o errores",
    icon: Scale,
    url: "/wms/adjustments",
    color: "bg-orange-50 text-orange-600 border-orange-200",
    superadminOnly: true,
  },
];

export const WmsLandingPage = () => {
  const navigate = useNavigate();
  const userRole = useProfileStore((s) => s.role);

  // Filter sections based on user role
  const visibleSections = sections.filter((section) => {
    if (section.superadminOnly && userRole !== "superadmin") return false;
    if (section.allowedRoles && !section.allowedRoles.includes(userRole ?? ""))
      return false;
    return true;
  });

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Logística</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gestión de almacén, inventario y despacho
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {visibleSections.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.url}
              onClick={() => navigate(section.url)}
              className={`flex flex-col items-start gap-3 rounded-xl border p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 ${section.color}`}
            >
              <Icon className="h-7 w-7" />
              <div>
                <h3 className="text-sm font-semibold">{section.title}</h3>
                <p className="mt-1 text-xs leading-relaxed opacity-70">
                  {section.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
